import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import AdminPage from '../../../admin/shared/AdminPage'
import ProvidersTab from './ProvidersTab'
import ModelsTab from './ModelsTab'
import TasksTab from './TasksTab'
import GuardrailsTab from './GuardrailsTab'
import { ToastProvider } from './ToastContext'

const TABS = [
  { id: 'providers', label: 'Providers' },
  { id: 'models', label: 'Models' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'guardrails', label: 'Guardrails' }
]

const DEFAULT_TAB = 'providers'
const VALID_TABS = new Set(TABS.map(t => t.id))

const useStyles = makeStyles(() => ({
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 2px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-sm) var(--border-radius-sm) 0 0',
    marginBottom: 'var(--margin-md)'
  },
  tab: {
    padding: 'var(--padding-sm) var(--padding-md)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 500,
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    borderBottom: '2px solid transparent',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    userSelect: 'none',
    outline: 'none',
    '&:hover': { color: 'var(--color-neutral-700)' },
    '&:focus-visible': {
      color: 'var(--color-neutral-700)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  tabActive: {
    color: 'var(--color-primary-700)',
    borderBottomColor: 'var(--color-primary-500)',
    fontWeight: 600
  }
}))

function ConfigurationHome () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const tabRefs = useRef([])

  const activeTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get('tab')
    return VALID_TABS.has(t) ? t : DEFAULT_TAB
  }, [location.search])

  const [providerFilter, setProviderFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')

  const goToTab = useCallback((nextTab, { replace = false } = {}) => {
    const params = new URLSearchParams(location.search)
    params.set('tab', nextTab)
    const target = { pathname: location.pathname, search: params.toString() }
    if (replace) history.replace(target)
    else history.push(target)
  }, [history, location.pathname, location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.get('tab')) {
      params.set('tab', DEFAULT_TAB)
      history.replace({ pathname: location.pathname, search: params.toString() })
    }
  }, [history, location.pathname, location.search])

  const drilldownToModels = useCallback((id) => {
    setProviderFilter(id || '')
    goToTab('models')
  }, [goToTab])

  const drilldownToTasks = useCallback((id) => {
    setModelFilter(id || '')
    goToTab('tasks')
  }, [goToTab])

  const onTabKeyDown = useCallback((event, index) => {
    let nextIndex = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = TABS.length - 1
    else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      goToTab(TABS[index].id)
      return
    }
    if (nextIndex != null) {
      event.preventDefault()
      goToTab(TABS[nextIndex].id)
      tabRefs.current[nextIndex]?.focus()
    }
  }, [goToTab])

  return (
    <AdminPage>
      <ToastProvider>
        <div className={classes.tabBar} role='tablist' aria-label='Denali AI configuration'>
          {TABS.map((tab, index) => {
            const isActive = activeTab === tab.id
            return (
              <div
                key={tab.id}
                ref={el => { tabRefs.current[index] = el }}
                role='tab'
                id={`config-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`config-tabpanel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
                onClick={() => goToTab(tab.id)}
                onKeyDown={(e) => onTabKeyDown(e, index)}
              >
                {tab.label}
              </div>
            )
          })}
        </div>

        <div
          role='tabpanel'
          id={`config-tabpanel-${activeTab}`}
          aria-labelledby={`config-tab-${activeTab}`}
        >
          {activeTab === 'providers' && (
            <ProvidersTab onDrilldownToModels={drilldownToModels} />
          )}
          {activeTab === 'models' && (
            <ModelsTab
              providerFilter={providerFilter}
              setProviderFilter={setProviderFilter}
              onDrilldownToTasks={drilldownToTasks}
            />
          )}
          {activeTab === 'tasks' && (
            <TasksTab
              modelFilter={modelFilter}
              setModelFilter={setModelFilter}
            />
          )}
          {activeTab === 'guardrails' && <GuardrailsTab />}
        </div>
      </ToastProvider>
    </AdminPage>
  )
}

export default ConfigurationHome
